import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, Tooltip, Divider, Avatar, Chip, Button, Alert
} from '@mui/material';
import { 
  AddCircle, Delete, Save, Edit, Person, Group, Apartment, 
  AdminPanelSettings, Security, DisplaySettings, QrCode,
  Settings, Dashboard, Storage as StorageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './axiosConfig';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import AlertSnackbar from './AlertSnackbar';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newRoomName, setNewRoomName] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [userRole, setUserRole] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [editRoles, setEditRoles] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchUserRole();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(`/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      showSnackbar('Error al cargar usuarios', 'error');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get(`/api/projects`);
      setProjects(response.data);
    } catch (error) {
      showSnackbar('Error al cargar proyectos', 'error');
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await axiosInstance.get(`/api/check-auth`);
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      await axiosInstance.post(`/api/admin/create-user`, {
        email: newUserEmail,
        password: newUserPassword,
        role: newUserRole,
        projectId: selectedProjectId || null,
      });
      showSnackbar('Usuario creado con éxito', 'success');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      fetchUsers();
    } catch (error) {
      showSnackbar('Error al crear usuario', 'error');
    }
  };

  const handleCreateProject = async () => {
    try {
      await axiosInstance.post(`/api/project`, { name: newProjectName });
      showSnackbar('Proyecto creado con éxito', 'success');
      setNewProjectName('');
      fetchProjects();
    } catch (error) {
      showSnackbar('Error al crear proyecto', 'error');
    }
  };

  const handleCreateRoom = async () => {
    try {
      await axiosInstance.post(`/api/room`, { name: newRoomName, projectId: selectedProjectId });
      showSnackbar('Room creada con éxito', 'success');
      setNewRoomName('');
    } catch (error) {
      showSnackbar('Error al crear room', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axiosInstance.delete(`/api/admin/delete-user/${userId}`);
      showSnackbar('Usuario eliminado con éxito', 'success');
      fetchUsers();
    } catch (error) {
      showSnackbar('Error al eliminar usuario', 'error');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/api/admin/update-role`, { userId, role: newRole });
      showSnackbar('Rol actualizado con éxito', 'success');
      fetchUsers();
    } catch (error) {
      showSnackbar('Error al actualizar rol', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin': return 'error';
      case 'admin': return 'warning';
      case 'grower': return 'info';
      default: return 'default';
    }
  };

  return (
    <ModernLayout title="Panel de Administración">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Panel de Administración
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona usuarios, proyectos y configuraciones del sistema
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Usuarios"
            value={users.length.toString()}
            subtitle="Usuarios registrados"
            icon={<Person />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Proyectos Activos"
            value={projects.length.toString()}
            subtitle="Proyectos en curso"
            icon={<Apartment />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Administradores"
            value={users.filter(user => user.role === 'admin' || user.role === 'superadmin').length.toString()}
            subtitle="Usuarios con privilegios"
            icon={<AdminPanelSettings />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Tu Rol"
            value={userRole}
            subtitle="Privilegios actuales"
            icon={<Security />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Quick Navigation */}
      <ModernCard
        title="Acceso Rápido"
        subtitle="Herramientas de administración"
        sx={{ mb: 3 }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DisplaySettings />}
              onClick={() => navigate('/admin/room-displays')}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Room Displays
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Gestionar pantallas de rooms
                </Typography>
              </Box>
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<QrCode />}
              onClick={() => navigate('/unifi-control')}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'success.main',
                '&:hover': {
                  borderColor: 'success.dark',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  UniFi Control
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Control de acceso y cámaras
                </Typography>
              </Box>
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => navigate('/project-settings')}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Configuración
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ajustes del proyecto
                </Typography>
              </Box>
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Dashboard />}
              onClick={() => navigate('/dashboard')}
              sx={{
                p: 2,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'info.main',
                '&:hover': {
                  borderColor: 'info.dark',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                }
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Dashboard
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Volver al dashboard
                </Typography>
              </Box>
            </Button>
          </Grid>
        </Grid>
      </ModernCard>

      {/* Database Management Card */}
      <ModernCard
        title="Gestión de Base de Datos"
        subtitle="Herramientas avanzadas de administración"
        sx={{ mb: 3 }}
      >
        <Box sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Nuevo:</strong> Gestiona tu base de datos directamente desde el dashboard. 
            Puedes ver el estado, comparar esquemas y ejecutar migraciones.
          </Alert>
          <Button
            fullWidth
            variant="contained"
            startIcon={<StorageIcon />}
            onClick={() => navigate('/admin/database-manager')}
            sx={{
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                🗄️ Gestor de Base de Datos
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Monitorear, actualizar y migrar tu base de datos
              </Typography>
            </Box>
          </Button>
        </Box>
      </ModernCard>

      <Grid container spacing={3}>
        {/* Crear Usuario */}
        <Grid item xs={12} md={4}>
          <ModernCard
            title="Crear Usuario"
            subtitle="Registra nuevos usuarios en el sistema"
          >
            <Box sx={{ p: 2 }}>
              <TextField
                label="Email"
                fullWidth
                margin="normal"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="usuario@ejemplo.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <TextField
                label="Contraseña"
                fullWidth
                margin="normal"
                type="password"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Contraseña"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Rol</InputLabel>
                <Select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="grower">Grower</MenuItem>
                  {userRole === 'superadmin' && <MenuItem value="admin">Admin</MenuItem>}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <GradientButton
                  startIcon={<AddCircle />}
                  onClick={handleCreateUser}
                  disabled={!newUserEmail || !newUserPassword}
                >
                  Crear Usuario
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>

        {/* Crear Proyecto */}
        {userRole === 'superadmin' && (
          <Grid item xs={12} md={4}>
            <ModernCard
              title="Crear Proyecto"
              subtitle="Crea nuevos proyectos en el sistema"
            >
              <Box sx={{ p: 2 }}>
                <TextField
                  label="Nombre del Proyecto"
                  fullWidth
                  margin="normal"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Nombre del Proyecto"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <GradientButton
                    startIcon={<AddCircle />}
                    onClick={handleCreateProject}
                    disabled={!newProjectName}
                  >
                    Crear Proyecto
                  </GradientButton>
                </Box>
              </Box>
            </ModernCard>
          </Grid>
        )}

        {/* Crear Room */}
        <Grid item xs={12} md={4}>
          <ModernCard
            title="Crear Room"
            subtitle="Añade espacios de cultivo a proyectos"
          >
            <Box sx={{ p: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Proyecto</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Nombre de la Room"
                fullWidth
                margin="normal"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="Nombre de la Room"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <GradientButton
                  startIcon={<AddCircle />}
                  onClick={handleCreateRoom}
                  disabled={!selectedProjectId || !newRoomName}
                >
                  Crear Room
                </GradientButton>
              </Box>
            </Box>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Users List */}
      <ModernCard
        title="Gestión de Usuarios"
        subtitle="Administra los usuarios del sistema"
        sx={{ mt: 3 }}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {users.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    {user.email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {user.email}
                    </Typography>
                    <Chip
                      label={user.role}
                      size="small"
                      color={getRoleColor(user.role)}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar rol">
                      <ModernButton
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => {
                          const newRole = user.role === 'user' ? 'grower' : 
                                        user.role === 'grower' ? 'admin' : 'user';
                          handleUpdateUserRole(user.id, newRole);
                        }}
                        variant="text"
                      />
                    </Tooltip>
                    <Tooltip title="Eliminar usuario">
                      <ModernButton
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteUser(user.id)}
                        variant="text"
                        color="error"
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </ModernCard>

      <AlertSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </ModernLayout>
  );
};

export default AdminDashboard;
